<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import dayjs from "dayjs";
import { parseErrors } from "@/assets/js/parseErrors";
import { useToastStore } from "@/store/toast";
import api from "@/api";

interface IDay {
	id: number;
	name: string;
	is_available_day?: number | null;
	is_available_night?: number | null;
}

interface IDateArr {
	isAvailableDay: number;
	isAvailableNight: number;
}

const props = defineProps<{
	tempId: number | null;
	patternStartDay: string;
}>();

const emit = defineEmits(["confirm", "close-modal"]);

const days = ref<IDay[]>([
	{ id: 1, name: "M" },
	{ id: 2, name: "T" },
	{ id: 3, name: "W" },
	{ id: 4, name: "T" },
	{ id: 5, name: "F" },
	{ id: 6, name: "S" },
	{ id: 7, name: "S" },
]);

const numberOfWeeks = ref("2");
const activeDay = ref<IDay | null>(null);
const isDayChangeVisible = ref(false);

const getSevenDayStr = computed(() => {
	return props.patternStartDay
		? dayjs(props.patternStartDay).add(6, "d").format("YYYY-MM-DD")
		: null;
});

const fetchOneAvailability = async () => {
	const params = {
		tempId: props.tempId,
		date_from: props.patternStartDay,
		date_to: getSevenDayStr.value,
	};

	try {
		const res = await api.getOneAvailability(params); // store.dispatch("getOneAvailability", payload);
		if (res.data && res.data.data && res.data.data.length) {
			populateMoreDayProps(res.data.data);
		}
	} catch (err: any) {
		if (err.response && err.response.data && err.response.data.message) {
			useToastStore().openToastError(err.response.data.message);
		}
		console.warn(err.message);
	}
};

const saveAvailabilitiesMultiple = async () => {
	const params = {
		tempId: props.tempId,
		date_from: props.patternStartDay,
		number_of_weeks: numberOfWeeks.value,
		days: formatDays(),
	};

	try {
		await api.saveMultiAvailabilities(params); // bstore.dispatch("saveMultiAvailabilities", params);
		return true;
	} catch (err: any) {
		console.log("[WARN]", err.message);
		const errs = parseErrors(err);
		if (errs) {
			useToastStore().openToastError(errs);
		}
	}
	return false;
};

const formatDays = () => {
	return days.value.map((day) => ({
		day: day.id,
		is_available_day: day.is_available_day,
		is_available_night: day.is_available_night,
	}));
};

const populateMoreDayProps = (dateArr: IDateArr[]) => {
	let firstDayIndex = new Date(props.patternStartDay).getDay();
	const sortDateArr = [...dateArr];
	while (firstDayIndex > 1 || firstDayIndex < 1) {
		if (firstDayIndex < 1) {
			sortDateArr.push(sortDateArr.shift());
			firstDayIndex++;
		} else {
			sortDateArr.unshift(sortDateArr.pop());
			firstDayIndex--;
		}
	}

	for (const [index, day] of days.value.entries()) {
		const isDayVal = sortDateArr[index]?.isAvailableDay ?? null;
		const isNightVal = sortDateArr[index]?.isAvailableNight ?? null;
		day.is_available_day = isDayVal;
		day.is_available_night = isNightVal;
	}
};

const isBtnSel = (num: number) => {
	const dayIndex = days.value.findIndex(
		(day) => day.id === activeDay.value?.id,
	);
	if (dayIndex === -1) {
		console.warn("Day not found");
		return false;
	}
	const day = days.value[dayIndex];
	switch (num) {
		case 1: {
			return day.is_available_day === 1;
		}
		case 2: {
			return day.is_available_night === 1;
		}
		case 3: {
			return day.is_available_day === 0;
		}
		case 4: {
			return day.is_available_night === 0;
		}
		default: {
			return false;
		}
	}
};

const changeDay = (day: IDay) => {
	activeDay.value = { ...day };
	isDayChangeVisible.value = false;
	nextTick(() => {
		isDayChangeVisible.value = true;
	});
};

const isActiveDay = (day: IDay) => {
	return day.id === activeDay.value?.id;
};

const setDay = (pos: number) => {
	const dayIndex = days.value.findIndex(
		(day) => day.id === activeDay.value?.id,
	);
	if (dayIndex === -1) {
		console.warn("Day not found");
		return;
	}
	const day = days.value[dayIndex];
	switch (pos) {
		case 1: {
			day.is_available_day = day.is_available_day ? null : 1;
			break;
		}
		case 2: {
			day.is_available_night = day.is_available_night ? null : 1;
			break;
		}
		case 3: {
			day.is_available_day = day.is_available_day === 0 ? null : 0;
			break;
		}
		case 4: {
			day.is_available_night = day.is_available_night === 0 ? null : 0;
			break;
		}
	}
};

const iconDayClass = (day: IDay) => {
	if (
		day.is_available_day === 1 &&
		(day.is_available_night === null || day.is_available_night === undefined)
	) {
		return "available-by-day";
	} else if (day.is_available_day === 1 && day.is_available_night === 0) {
		return "available-by-day-not-by-night";
	} else if (day.is_available_day === 1 && day.is_available_night === 1) {
		return "available-all-day";
	} else if (
		day.is_available_day === 0 &&
		(day.is_available_night === null || day.is_available_night === undefined)
	) {
		return "not-available-by-day";
	} else if (day.is_available_day === 0 && day.is_available_night === 0) {
		return "not-available-all-day";
	} else if (day.is_available_day === 0 && day.is_available_night === 1) {
		return "available-by-night-not-by-day";
	} else if (
		(day.is_available_day === null || day.is_available_day === undefined) &&
		day.is_available_night === 0
	) {
		return "not-available-all-night";
	} else if (
		(day.is_available_day === null || day.is_available_day === undefined) &&
		day.is_available_night === 1
	) {
		return "available-only-by-night";
	}
	return "icon";
};

const callModalSubmit = async () => {
	const isDone = await saveAvailabilitiesMultiple();
	if (isDone) {
		emit("confirm");
		emit("close-modal");
	}
};

const callModalClose = () => {
	emit("close-modal");
};

onMounted(() => {
	fetchOneAvailability();
});
</script>

<template>
	<div class="pattern-wrap">
		<div class="days">
			<span
				v-for="day in days"
				:key="day.id"
				class="day"
				:class="{ active: isActiveDay(day) }"
				@click="changeDay(day)"
			>
				<span class="name">{{ day.name }}</span>
				<span class="icon-wrap"><span :class="iconDayClass(day)"></span></span>
			</span>
		</div>
		<transition name="slide-down">
			<div
				v-if="isDayChangeVisible"
				class="one-day-change"
			>
				<div class="row">
					<span>AVAILABLE</span>
					<div
						class="button-patern"
						:class="{ selected: isBtnSel(1) }"
						@click="setDay(1)"
					>
						<span class="available-by-day"></span>
						<span>DAY</span>
					</div>
					<div
						class="button-patern"
						:class="{ selected: isBtnSel(2) }"
						@click="setDay(2)"
					>
						<span class="available-only-by-night"></span>
						<span>NIGHT</span>
					</div>
				</div>
				<div class="row">
					<span>UNAVAILABLE</span>
					<div
						class="button-patern"
						:class="{ selected: isBtnSel(3) }"
						@click="setDay(3)"
					>
						<span class="not-available-by-day"></span>
						<span>DAY</span>
					</div>
					<div
						class="button-patern"
						:class="{ selected: isBtnSel(4) }"
						@click="setDay(4)"
					>
						<span class="not-available-all-night"></span>
						<span>NIGHT</span>
					</div>
				</div>
			</div>
		</transition>
		<div class="action">
			Assign pattern to
			<input
				v-model="numberOfWeeks"
				class="input"
				type="text"
			/>
			weeks.
		</div>
		<!-- v-facade="'####'" -->
		<div class="buttons-centered">
			<button
				class="button is-small is-generic-app-blue is-caps-lock"
				type="submit"
				@click.prevent="callModalSubmit"
			>
				Submit
			</button>
			<button
				class="button is-small is-outlined is-caps-lock"
				@click.prevent="callModalClose"
			>
				Cancel
			</button>
		</div>
	</div>
</template>

<style lang="scss" scoped>
$available: #3999fa;
$unavailable: #f7a53f;
.pattern-wrap {
	box-sizing: border-box;
	padding: 0 20px 20px;
	min-width: 412px;

	.one-day-change {
		margin: 20px 0;
		span {
			padding: 6px;
			margin: 0;
		}
		.row {
			display: flex;
			margin-bottom: 5px;

			> span {
				width: 120px;
				text-align: right;
			}
		}

		.button-patern {
			height: 100%;
			width: 100px;
			margin: 0 5px;
			display: flex;
			align-items: center;
			justify-content: center;
			border: $border;
			border-radius: $button-border-radius;

			span:first-child {
				height: 20px;
				width: 20px;
			}

			&.selected {
				background: #e1e1e1;
				border: 1px solid #a2a2a2;
			}
		}

		span.available-only-by-night {
			border-radius: 50%;
			background: #fff;
			transform: rotate(60deg);
			background-image: linear-gradient(90deg, transparent 50%, $available 0);
		}

		span.available-by-day {
			border-radius: 50%;
			background: $available;
			transform: rotate(60deg);
			background-image: linear-gradient(90deg, transparent 50%, #fff 0);
		}

		span.not-available-all-night {
			border-radius: 50%;
			background: #fff;
			transform: rotate(60deg);
			background-image: linear-gradient(90deg, transparent 50%, $unavailable 0);
		}

		span.not-available-by-day {
			border-radius: 50%;
			background: $unavailable;
			transform: rotate(60deg);
			background-image: linear-gradient(90deg, transparent 50%, #fff 0);
		}
	}
	.days {
		margin: 10px 0;
		display: flex;
		justify-content: center;

		.day {
			display: flex;
			flex-direction: column;
			align-items: center;
			margin: 0 3.5px 2px;
			user-select: none;

			.name {
				margin-bottom: 3px;
				// width: 100%;
				width: 21px;
				height: 21px;
				text-align: center;
				cursor: pointer;
			}

			&.active {
				.name {
					background: #8bbbe1;
					border-radius: 10px;
				}
			}

			.icon-wrap {
				cursor: pointer;
				width: 20px;
				height: 20px;

				> span {
					display: inline-block;
					height: 100%;
					width: 100%;
				}

				span.available-all-day {
					border: 1px solid $available;
					border-radius: 50%;
					background: $available;
				}

				span.not-available-all-day {
					border: 1px solid $unavailable;
					border-radius: 50%;
					background: $unavailable;
				}

				span.available-only-by-night {
					border-radius: 50%;
					background: #fff;
					transform: rotate(60deg);
					background-image: linear-gradient(
						90deg,
						transparent 50%,
						$available 0
					);
				}

				span.available-by-day {
					border-radius: 50%;
					background: $available;
					transform: rotate(60deg);
					background-image: linear-gradient(90deg, transparent 50%, #fff 0);
				}

				span.available-by-day-not-by-night {
					border-radius: 50%;
					background: $available;
					transform: rotate(60deg);
					background-image: linear-gradient(
						90deg,
						transparent 50%,
						$unavailable 0
					);
				}

				span.available-by-night-not-by-day {
					border-radius: 50%;
					background: $unavailable;
					transform: rotate(60deg);
					background-image: linear-gradient(
						90deg,
						transparent 50%,
						$available 0
					);
				}

				span.not-available-all-night {
					border-radius: 50%;
					background: #fff;
					transform: rotate(60deg);
					background-image: linear-gradient(
						90deg,
						transparent 50%,
						$unavailable 0
					);
				}

				span.not-available-by-day {
					border-radius: 50%;
					background: $unavailable;
					transform: rotate(60deg);
					background-image: linear-gradient(90deg, transparent 50%, #fff 0);
				}

				.icon {
					background: url("data:image/svg+xml;base64,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")
						no-repeat;
					background-size: 100% 100%;
				}
			}
		}
	}

	.action {
		display: flex;
		align-items: center;
		justify-content: center;
		margin: 0 0 20px;

		input {
			margin: 0 5px;
			width: 80px;
		}
	}

	.slide-down-enter-active {
		transition: all 0.6s;
	}

	.slide-down-leave-active {
		transition: all 0.3s;
	}

	.slide-down-enter {
		transform: translateY(-20px);
	}

	.slide-down-leave-to {
		transform: translateY(-20px);
	}

	.slide-down-leave {
		height: 0;
	}
}
</style>
